import { __decorate } from "tslib";
import Vue from 'vue';
import Component from 'vue-class-component';
import moment from 'moment';
import ContractTemplateEditor from '@/components/ContractTemplateEditor.vue';
import DocumentTemplateEditor from '@/components/DocumentTemplateEditor.vue';
import SignatureCanvas from '@/components/SignatureCanvas.vue';
import ConfirmDialog from '@/components/ConfirmDialog.vue';
import Snackbar from '@/components/Snackbar.vue';
import { CONTRACT_STATUS_TITLES } from '@/constants/ContractTypes';
import { DOCUMENT_STATUS_TITLES } from '@/constants/DocumentTypes';
import { formatContractDate, renderPreview, SAMPLE_PLACEHOLDER_VALUES } from '@/constants/ContractPlaceholders';
import { getSeasonYear } from '@/store/seasonYear';
let Contracts = class Contracts extends Vue {
    tab = 0;
    working = false;
    editorOpen = false;
    editorBase = null;
    previewOpen = false;
    previewHtml = '';
    publishOpen = false;
    publishTarget = null;
    publishDocumentTarget = null;
    requiresResign = false;
    invalidatingId = null;
    pdfId = null;
    countersignOpen = false;
    countersignTarget = null;
    signature = null;
    documentEditorOpen = false;
    documentEditorBase = null;
    documentCountersignOpen = false;
    documentCountersignTarget = null;
    templateHeaders = [
        { text: 'Klíč', value: 'data.key' },
        { text: 'Název', value: 'data.title' },
        { text: 'Verze', value: 'data.version' },
        { text: 'Platnost', value: 'period', sortable: false },
        { text: 'Stav', value: 'data.published' },
        { text: 'Vytvořeno', value: 'data.createdAt' },
        { text: '', value: 'actions', sortable: false },
    ];
    documentTemplateHeaders = [
        { text: 'Klíč', value: 'data.key' },
        { text: 'Název', value: 'data.title' },
        { text: 'Verze', value: 'data.version' },
        { text: 'Platnost', value: 'period', sortable: false },
        { text: 'Stav', value: 'data.published' },
        { text: 'Vytvořeno', value: 'data.createdAt' },
        { text: '', value: 'actions', sortable: false },
    ];
    documentHeaders = [
        { text: 'Instruktor', value: 'instructor' },
        { text: 'Platnost', value: 'period', sortable: false },
        { text: 'Stav', value: 'data.status' },
        { text: 'Rozhodnuto', value: 'data.decidedAt' },
        { text: 'Verze šablony', value: 'data.templateVersion' },
        { text: '', value: 'actions', sortable: false },
    ];
    contractHeaders = [
        { text: 'Instruktor', value: 'instructor' },
        { text: 'Klíč', value: 'data.key' },
        { text: 'Platnost', value: 'period', sortable: false },
        { text: 'Stav', value: 'data.status' },
        { text: 'Podepsáno', value: 'data.instructorSignature.signedAt' },
        { text: 'Verze šablony', value: 'data.templateVersion' },
        { text: '', value: 'actions', sortable: false },
    ];
    get templates() {
        return this.$store.getters['contract/templates'];
    }
    get contracts() {
        return this.$store.getters['contract/contracts'];
    }
    get documentTemplates() {
        return this.$store.getters['document/templates'];
    }
    get documents() {
        return this.$store.getters['document/documents'];
    }
    documentStatusTitle(status) {
        return DOCUMENT_STATUS_TITLES[status] ?? status;
    }
    documentStatusColor(status) {
        return { signed: 'green', signed_by_instructor: 'orange', declined: 'blue-grey', invalidated: 'grey' }[status] ?? 'grey';
    }
    statusTitle(status) {
        return CONTRACT_STATUS_TITLES[status] ?? status;
    }
    statusColor(status) {
        return { signed: 'green', signed_by_instructor: 'orange', invalidated: 'grey' }[status] ?? 'grey';
    }
    instructorName(id) {
        const instructor = this.$store.getters['instructor/get'](id);
        return instructor ? `${instructor.data.name} ${instructor.data.surname}` : id;
    }
    formatTimestamp(value) {
        return value ? moment(value.toDate()).format('D. M. YYYY HH:mm') : '';
    }
    openEditor(base) {
        this.editorBase = base;
        this.editorOpen = true;
    }
    periodTitle(template) {
        const from = formatContractDate(template.data.validFrom);
        const to = formatContractDate(template.data.validTo);
        if (!from && !to)
            return 'Neomezeno';
        return `${from || '…'} – ${to || '…'}`;
    }
    contractPeriod(item) {
        const template = this.templates.find(t => t.id === item.data.templateId);
        return template ? this.periodTitle(template) : '';
    }
    documentPeriod(item) {
        const template = this.documentTemplates.find(t => t.id === item.data.templateId);
        return template ? this.periodTitle(template) : '';
    }
    openSamplePreview(bodyHtml, validFrom, validTo) {
        this.previewHtml = renderPreview(bodyHtml, {
            ...SAMPLE_PLACEHOLDER_VALUES,
            contractDate: moment().format('D. M. YYYY'),
            seasonYear: getSeasonYear(),
            ...(validFrom ? { validFrom: formatContractDate(validFrom) } : {}),
            ...(validTo ? { validTo: formatContractDate(validTo) } : {}),
        });
        this.previewOpen = true;
    }
    preview(template) {
        this.openSamplePreview(template.data.bodyHtml, template.data.validFrom, template.data.validTo);
    }
    askPublish(template) {
        this.publishTarget = template;
        this.publishDocumentTarget = null;
        this.requiresResign = false;
        this.publishOpen = true;
    }
    askPublishDocument(template) {
        this.publishDocumentTarget = template;
        this.publishTarget = null;
        this.requiresResign = false;
        this.publishOpen = true;
    }
    async publish() {
        const target = this.publishTarget ?? this.publishDocumentTarget;
        if (!target || this.working)
            return;
        this.working = true;
        try {
            await this.$store.dispatch(this.publishTarget ? 'contract/publishTemplate' : 'document/publishTemplate', {
                templateId: target.id,
                requiresResign: this.requiresResign,
            });
            Snackbar.success('Šablona publikována');
            this.publishOpen = false;
        }
        catch (e) {
            console.error(e);
            Snackbar.error(e.message || 'Publikace se nezdařila');
        }
        finally {
            this.working = false;
        }
    }
    async destroyTemplate(template) {
        await ConfirmDialog.run(() => this.$store.dispatch('contract/destroyTemplate', template), 'Smazat šablonu', `Opravdu smazat verzi ${template.data.version}?`);
    }
    openDocumentEditor(base) {
        this.documentEditorBase = base;
        this.documentEditorOpen = true;
    }
    async destroyDocumentTemplate(template) {
        await ConfirmDialog.run(() => this.$store.dispatch('document/destroyTemplate', template), 'Smazat dokument', `Opravdu smazat verzi ${template.data.version}?`);
    }
    openDocumentCountersign(document) {
        this.documentCountersignTarget = document;
        this.signature = null;
        this.documentCountersignOpen = true;
    }
    async countersignDocument() {
        if (!this.documentCountersignTarget || !this.signature || this.working)
            return;
        this.working = true;
        try {
            await this.$store.dispatch('document/countersign', {
                documentId: this.documentCountersignTarget.id,
                signaturePng: this.signature,
            });
            Snackbar.success('Prohlášení podepsáno');
            this.documentCountersignOpen = false;
        }
        catch (e) {
            console.error(e);
            Snackbar.error(e.message || 'Podpis se nezdařil');
        }
        finally {
            this.working = false;
        }
    }
    async downloadDocumentPdf(document) {
        this.pdfId = document.id;
        try {
            window.open(await this.$store.dispatch('document/pdfUrl', document), '_blank');
        }
        catch (e) {
            console.error(e);
            Snackbar.error('PDF se nepodařilo stáhnout');
        }
        finally {
            this.pdfId = null;
        }
    }
    async invalidateDocument(document) {
        if (this.invalidatingId)
            return;
        if (!await ConfirmDialog.open('Zneplatnit prohlášení', 'Záznam se označí jako neplatný. Zdanění je potřeba upravit ručně. Pokračovat?'))
            return;
        this.invalidatingId = document.id;
        try {
            await this.$store.dispatch('document/invalidate', document.id);
            Snackbar.success('Prohlášení zneplatněno');
        }
        catch (e) {
            console.error(e);
            Snackbar.error(e.message || 'Zneplatnění se nezdařilo');
        }
        finally {
            this.invalidatingId = null;
        }
    }
    openCountersign(contract) {
        this.countersignTarget = contract;
        this.signature = null;
        this.countersignOpen = true;
    }
    async countersign() {
        if (!this.countersignTarget || !this.signature || this.working)
            return;
        this.working = true;
        try {
            await this.$store.dispatch('contract/countersign', {
                contractId: this.countersignTarget.id,
                signaturePng: this.signature,
            });
            Snackbar.success('Smlouva podepsána a odeslána instruktorovi');
            this.countersignOpen = false;
        }
        catch (e) {
            console.error(e);
            Snackbar.error(e.message || 'Podpis se nezdařil');
        }
        finally {
            this.working = false;
        }
    }
    async downloadPdf(contract) {
        this.pdfId = contract.id;
        try {
            window.open(await this.$store.dispatch('contract/pdfUrl', contract), '_blank');
        }
        catch (e) {
            console.error(e);
            Snackbar.error('PDF se nepodařilo stáhnout');
        }
        finally {
            this.pdfId = null;
        }
    }
    async invalidate(contract) {
        if (this.invalidatingId)
            return;
        if (!await ConfirmDialog.open('Zneplatnit smlouvu', 'Instruktor bude muset smlouvu podepsat znovu. Pokračovat?'))
            return;
        this.invalidatingId = contract.id;
        try {
            await this.$store.dispatch('contract/invalidate', contract.id);
            Snackbar.success('Smlouva zneplatněna');
        }
        catch (e) {
            console.error(e);
            Snackbar.error(e.message || 'Zneplatnění se nezdařilo');
        }
        finally {
            this.invalidatingId = null;
        }
    }
};
Contracts = __decorate([
    Component({ components: { ContractTemplateEditor, DocumentTemplateEditor, SignatureCanvas } })
], Contracts);
export default Contracts;
